import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTranslatedText } from '@/hooks/useAutoTranslate';

interface Banner {
  id: number;
  title: string;
  subtitle: string;
  img_url: string;
  link_url: string;
  link_text: string;
}

const DEFAULT_BANNERS: Banner[] = [
  {
    id: 1,
    title: '吉尔吉斯斯坦 · 合法代孕 · 全球服务',
    subtitle: '',
    img_url: 'https://readdy.ai/api/search-image?query=elegant%20warm%20medical%20clinic%20interior%20with%20soft%20natural%20light%2C%20modern%20fertility%20clinic%20lobby%2C%20warm%20champagne%20gold%20and%20white%20tones%2C%20professional%20healthcare%20environment%2C%20blurred%20background%2C%20beautiful%20couple%20silhouette%2C%20high-end%20medical%20facility%20ambiance%2C%20serene%20and%20hopeful%20atmosphere%2C%20soft%20bokeh%2C%20ultra%20wide%20angle%20panoramic%20view&width=1600&height=900&seq=hero-bg-001&orientation=landscape',
    link_url: '/contact',
    link_text: '立即预约咨询',
  },
];

const stats = [
  { key: 'hero_stat1', endValue: 500, suffix: '+', label: '成功案例' },
  { key: 'hero_stat2', endValue: 30, suffix: '+', label: '合作医院' },
  { key: 'hero_stat3', endValue: 10, suffix: '+', label: '服务年限' },
  { key: 'hero_stat4', endValue: 15, suffix: '+', label: '国家客户' },
];

function useCountUp(end: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start]);

  return count;
}

function StatItem({ stat, animate }: { stat: typeof stats[0]; animate: boolean }) {
  const { t } = useTranslation();
  const count = useCountUp(stat.endValue, 1800, animate);
  return (
    <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-lg md:rounded-xl p-2.5 md:p-4 text-center transition-transform hover:scale-105">
      <div className="text-lg md:text-2xl lg:text-3xl font-bold text-[#c9a96e] mb-0.5 md:mb-1">
        {animate ? count : 0}{stat.suffix}
      </div>
      <div className="text-white/70 text-[10px] md:text-xs">{t(stat.key)}</div>
    </div>
  );
}

export default function HeroSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load banners from DB
  useEffect(() => {
    supabase
      .from('banners')
      .select('id, title, subtitle, img_url, link_url, link_text')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setBanners(data as Banner[]);
        } else {
          setBanners(DEFAULT_BANNERS);
        }
        setLoaded(true);
      });
  }, []);

  // Auto-play carousel
  useEffect(() => {
    if (banners.length <= 1) return;
    autoPlayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % banners.length);
    }, 6000);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [banners.length]);

  const goTo = (index: number) => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    setActiveIndex(index);
    if (banners.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % banners.length);
      }, 6000);
    }
  };

  const prevSlide = () => goTo((activeIndex - 1 + banners.length) % banners.length);
  const nextSlide = () => goTo((activeIndex + 1) % banners.length);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (!bgRef.current) return;
      const scrollY = window.scrollY;
      bgRef.current.style.transform = `translateY(${scrollY * 0.4}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection observer for count-up trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const currentBanner = banners[activeIndex] || DEFAULT_BANNERS[0];

  // Auto-translate banner dynamic content
  const trTitle = useTranslatedText(currentBanner.title);
  const trSubtitle = useTranslatedText(currentBanner.subtitle || '');
  const trLinkText = useTranslatedText(currentBanner.link_text || '');

  return (
    <section ref={sectionRef} className="relative min-h-[420px] sm:min-h-[480px] md:min-h-screen flex items-center overflow-hidden">
      {/* Parallax Background with crossfade */}
      <div className="absolute inset-0 overflow-hidden">
        <div ref={bgRef} className="absolute inset-0 scale-110 will-change-transform">
          {banners.map((b, i) => (
            <div
              key={b.id}
              className="absolute inset-0 transition-opacity duration-1000"
              style={{ opacity: i === activeIndex ? 1 : 0 }}
            >
              <img
                src={b.img_url}
                alt={b.title}
                className="w-full h-full object-cover object-center md:object-top"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2137]/88 via-[#0d2137]/65 to-[#0d2137]/30"></div>
        </div>
      </div>

      {/* Decorative animated orbs */}
      <div className="absolute top-1/4 right-1/4 w-48 h-48 md:w-80 md:h-80 rounded-full bg-[#c9a96e]/5 blur-3xl animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/3 w-40 h-40 md:w-64 md:h-64 rounded-full bg-white/5 blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }}></div>

      {/* Carousel controls */}
      {loaded && banners.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/25 transition-all cursor-pointer"
            aria-label={t('hero_prev_slide')}
          >
            <i className="ri-arrow-left-s-line text-base md:text-lg"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/25 transition-all cursor-pointer"
            aria-label={t('hero_next_slide')}
          >
            <i className="ri-arrow-right-s-line text-base md:text-lg"></i>
          </button>
        </>
      )}

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-10 md:pt-24 md:pb-16">
        <div className="max-w-2xl">
          {/* Tag */}
          <div
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-[10px] md:text-xs font-medium px-2.5 py-0.5 md:px-4 md:py-1.5 rounded-full mb-3 md:mb-6"
            style={{ animation: 'fadeInUp 0.6s ease forwards' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#c9a96e] inline-block animate-pulse shrink-0"></span>
            <span className="truncate max-w-[200px] md:max-w-none">{trTitle || currentBanner.title}</span>
          </div>

          {/* Title */}
          <h2
            className="text-2xl md:text-5xl lg:text-6xl font-bold text-white mb-1 md:mb-2 leading-tight whitespace-nowrap"
            style={{ animation: 'fadeInUp 0.7s ease 0.1s both' }}
          >
            {t('hero_title1')}
          </h2>
          <h1
            className="text-2xl md:text-5xl lg:text-6xl font-bold text-[#c9a96e] mb-3 md:mb-6 leading-tight whitespace-nowrap"
            style={{ animation: 'fadeInUp 0.7s ease 0.2s both' }}
          >
            {t('hero_title2')}
          </h1>
          <p
            className="text-white/80 text-xs md:text-base lg:text-lg leading-relaxed mb-5 md:mb-10 max-w-xl"
            style={{ animation: 'fadeInUp 0.7s ease 0.3s both' }}
          >
            {trSubtitle || t('hero_subtitle')}
          </p>

          {/* CTA Buttons */}
          <div
            className="flex flex-wrap gap-2 md:gap-4 mb-6 md:mb-14"
            style={{ animation: 'fadeInUp 0.7s ease 0.4s both' }}
          >
            <Link
              to={currentBanner.link_url || '/contact'}
              className="flex items-center gap-1.5 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-4 py-2 md:px-7 md:py-3.5 rounded-full transition-all hover:scale-105 whitespace-nowrap cursor-pointer text-xs md:text-base"
            >
              <i className="ri-calendar-check-line"></i>
              {trLinkText || currentBanner.link_text || t('hero_cta1')}
            </Link>
            <Link
              to="/services"
              className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/30 text-white font-semibold px-4 py-2 md:px-7 md:py-3.5 rounded-full transition-all whitespace-nowrap cursor-pointer text-xs md:text-base"
            >
              <i className="ri-arrow-right-line"></i>
              {t('hero_cta2')}
            </Link>
          </div>

          {/* Stats with Count-Up Animation */}
          <div
            ref={statsRef}
            className="grid grid-cols-4 gap-1.5 md:gap-4"
            style={{ animation: 'fadeInUp 0.7s ease 0.5s both' }}
          >
            {stats.map((stat) => (
              <StatItem key={stat.key} stat={stat} animate={statsVisible} />
            ))}
          </div>
        </div>
      </div>

      {/* Dots indicator */}
      {loaded && banners.length > 1 && (
        <div className="absolute bottom-3 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 md:gap-2">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`transition-all rounded-full cursor-pointer ${
                i === activeIndex ? 'w-4 h-1 md:w-6 md:h-2 bg-[#c9a96e]' : 'w-1 h-1 md:w-2 md:h-2 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={t('hero_slide_to', { n: i + 1 })}
            ></button>
          ))}
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}